import React, { useEffect, useState, useContext, useMemo, memo } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { TextField, Button, Container, Box, Typography, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";    
import LogoGrupoLae from '../../img/image_2024_04_30T07_32_45_647Z.png';
import LogoTSP from '../../img/LOGO_TSP_2.png';
import BackgroundImage from '../../img/Fondo_GRUPO_LAE.jpg';
import { ToastContainer, toast } from "react-toastify";
import { fetchLogin } from '../../../peticiones_API/PeticionesAuth';
import OTPInput from '../basicos/OTPInput';
import { UsuarioContext } from "../../providers/personaContext";
import { jwtDecode } from "jwt-decode";
import { clearCache } from '../../../funciones/apiCache';
import { consumirDestinoRetorno, destinoDesdeLocationState } from '../../../funciones/rutaRetorno';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSnowPreset } from "@tsparticles/preset-snow";
import { loadFireworksPreset } from "@tsparticles/preset-fireworks";

const isTSP = window.location.hostname.startsWith('tsp.');
const Logo = isTSP ? LogoTSP : LogoGrupoLae;

const ParticlesBackground = memo(({ particlesEffect, particlesOptions }) => {
    if (!particlesEffect || !particlesOptions) return null;
    
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1,
            pointerEvents: 'none'
        }}>
            <Particles
                id="tsparticles"
                options={particlesOptions}
            />
        </div>
    );
});
 
const FormLogin = ({ setAutenticado }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [inicializado, setInicializado] = useState(false);
    const [useBackupCode, setUseBackupCode] = useState(false);
    const location = useLocation();
    const [parametrosBusqueda] = useSearchParams();
 
    const navigate = useNavigate();
    const persona = useContext(UsuarioContext);

    useEffect(() => {
        initParticlesEngine(async (engine) => {
            await loadSnowPreset(engine);
            await loadFireworksPreset(engine);
        }).then(() => {
            setInicializado(true);
        });
    }, []);

    // Determinar qué efecto de partículas mostrar según la fecha
    const particlesEffect = useMemo(() => {
        const hoy = new Date();
        const mes = hoy.getMonth(); // 0-11 (Enero es 0, Diciembre es 11)
        const dia = hoy.getDate();

        // Del 31 de diciembre (incluido) al 3 de enero (incluido): fuegos artificiales
        if ((mes === 11 && dia === 31) || (mes === 0 && dia >= 1 && dia <= 3)) {
            return 'fireworks';
        }
        // Durante todo diciembre: nieve
        if ((mes === 11 && dia >= 1) || (mes === 0 && dia <= 6)) {
            return 'snow';
        }
        return null;
    }, []);

    // Configuración de partículas según el efecto
    const particlesOptions = useMemo(() => {
        if (particlesEffect === 'snow') {
            return {
                preset: "snow",
                background: {
                    opacity: 0
                }
            };
        } else if (particlesEffect === 'fireworks') {
            return {
                preset: "fireworks",
                background: {
                    color: "#000000ff"
                },
                sounds: {
                    enable: false
                }
            };
        }
        return null;
    }, [particlesEffect]);
 
    useEffect(() => {
        console.log(location.state);
        if (localStorage.getItem("token")) {
            let decoded;
            try {
                decoded = jwtDecode(localStorage.getItem("token"));
            } catch (error) {
                localStorage.removeItem("token");
                localStorage.removeItem("lastActivity");
                localStorage.removeItem("numActivo");
                setAutenticado(false);
                return;
            }
 
            try {
                setAutenticado(false);
                localStorage.removeItem("token");
                localStorage.removeItem("lastActivity");
                localStorage.removeItem("numActivo");
            } catch (error) {
                setAutenticado(false);
                localStorage.removeItem("token");
                localStorage.removeItem("lastActivity");
                localStorage.removeItem("numActivo");
            }
        }
    }, [navigate, setAutenticado, location.state]);
 
    const completeLogin = (data) => {
        toast.success("Usuario logueado correctamente");
        clearCache();
        localStorage.setItem("token", data.token);
        persona.actualizarPersona();
        setAutenticado(true);

        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission().then(permission => {
                if (permission === 'granted') {
                    console.log('Permisos de notificación concedidos');
                }
            }).catch(error => {
                console.error('Error al solicitar permisos:', error);
            });
        }

        /* Fase 13 · aquí estaba el FIXME de «descomentar cuando se quiera forzar el 2FA».
         *
         * Borrado con el cerrojo entero. Era lo más peligroso que había: tres consumidores vivos
         * —la redirección de `App.js`, el modo del perfil, el borrado de aquí— y este único
         * productor comentado. Quien lo descomentara creyendo que activaba algo opcional
         * **activaba un cerrojo que encierra a la gente en el perfil**.
         *
         * Y la instrucción que daba —«cuando se quiera forzar»— apuntaba a un futuro que la
         * decisión de sacar el 2FA del login ha cancelado. */

        // Destino de retorno, por precedencia: sessionStorage (sobrevive a los
        // tres bordes) → el `from` de location.state. Ambos pasan por el
        // validador; `destino` queda en null si no había ninguno.
        const destino =
            consumirDestinoRetorno() ||
            destinoDesdeLocationState(location.state?.from);

        // Desajuste de versión: hay que recargar para servir el bundle nuevo.
        // Un reload seco perdería el destino (destruye el estado de navegación).
        // CON destino validado saltamos DURO a él: recarga la app Y aterriza
        // donde toca en un solo movimiento (el navigationFallback sirve la ruta
        // profunda; el token ya está en localStorage y sobrevive a la
        // navegación). SIN destino, el reload de siempre — nunca assign(undefined).
        const version = data.version;
        if (version !== process.env.REACT_APP_VERSION) {
            if (destino) window.location.assign(destino);
            else window.location.reload();
            return;
        }

        navigate(destino || '/panel-inicio', { replace: true });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!email || !password) {
            toast.error("¡Los campos no son validos!");
        } else {
            setLoading(true);
            try {
                const auth = await fetchLogin({ email: email, password: password });
                if (auth.data.token) {
                    completeLogin(auth.data);
                } else {
                    toast.error("¡Usuario o contraseña incorrectos!");
                }
            } catch (error) {
                console.log(error);
                if(error.response && error.response.status === 418)
                    toast.error(error.response.data?.message || "¡No tienes permisos para acceder!");
                else
                    toast.error("¡Ha ocurrido un error!");
            } finally {
                setLoading(false);
            }
        }
    };

    /* Fase 13 · aquí estaba `handleSubmitOTP`, el segundo paso del login.
     *
     * El segundo factor sale del login: prueba que fuiste tú **al abrir la sesión**, y una sesión
     * robada ya pasó ese control. El de la puerta prueba que sigues siendo tú **ahora mismo**, y
     * ése se queda — Soporte, RRHH y el listado de contraseñas lo siguen exigiendo.
     *
     * Quien lo tenga configurado no pierde nada: su secreto sigue ahí y es el que abre las puertas.
     * Lo único que cambia es que ya no se le pide dos veces. */

 
    const handleClickShowPassword = () => setShowPassword((show) => !show);
 
    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: particlesEffect === 'fireworks' ? 'none' : `url(${BackgroundImage})`,
                backgroundColor: particlesEffect === 'fireworks' ? '#000000ff' : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                position: 'relative'
            }}
        >
            {inicializado && <ParticlesBackground particlesEffect={particlesEffect} particlesOptions={particlesOptions} />}
            <Container
                component="main"
                maxWidth="xs"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 2
                }}
            >
                <ToastContainer />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 3,
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        width: '100%',
                        maxWidth: 360
                    }}
                >
                    <img src={Logo} alt="Logo" style={{ width: '130px', marginBottom: '20px' }} />
                    <Typography component="h1" variant="h5">
                        Iniciar Sesión
                    </Typography>

                    {                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label="Correo Electrónico"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label="Contraseña"
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2, py: 1 }}
                            >
                                {loading ? (
                                    <CircularProgress
                                        thickness={5}
                                        size={24}
                                        style={{ color: '#ffffff', position: 'relative', top: 3, marginBottom: 5 }}
                                    />
                                ) : (
                                    'Iniciar Sesión'
                                )}
                            </Button>

                            <Typography
                                variant="body2"
                                color="textSecondary"
                                align="center"
                                sx={{ mt: 1 }}
                            >
                                <Link
                                    to="/cambiarPassword"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                                    ¿Has olvidado la contraseña?
                                </Link>
                            </Typography>
                        </Box>
                    }
                </Box>
            </Container>
        </Box>
    );
};
 
export default FormLogin;